/**
 * The data-provider interface: what `useStage()` was for the Stage demo, `useData()` is for the product.
 *
 * `DataProvider` is composed of one slice per feature (each owned by that feature in `features/<x>/provider.ts`) plus
 * the core session + entity slices. Two implementations, same interface:
 *   - live:    every call goes to the US API through core/http.ts (`/api/v1/app/*`, same origin, cookie session);
 *   - fixture: an in-memory "Lone Star Mobile Auto" tenant (core/fixtures/db.ts) with a running replay, for building
 *              before P1's W0 lands and for demos. Only available in dev or when built with VITE_FIXTURES=1.
 * Views never call fetch directly; they use TanStack Query hooks over `useData().data.<slice>`.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { http } from './http';
import type { FixtureDb } from './fixtures/db';
import { liveSession, type SessionApi } from './session';
import { liveEntities, type EntitiesApi } from './entities';
import * as leads from '../features/leads/provider';
import * as calls from '../features/calls/provider';
import * as jobs from '../features/jobs/provider';
import * as dispatch from '../features/dispatch/provider';
import * as techs from '../features/techs/provider';
import * as customers from '../features/customers/provider';
import * as payments from '../features/payments/provider';
import * as passport from '../features/passport/provider';
import * as parts from '../features/parts/provider';
import * as fleet from '../features/fleet/provider';
import * as today from '../features/today/provider';
import * as analytics from '../features/analytics/provider';
import * as settings from '../features/settings/provider';
import * as onboarding from '../features/onboarding/provider';
import * as crm from '../features/dealer/crmProvider';
import * as deals from '../features/dealer/dealsProvider';
import * as scheduler from '../features/dealer/schedulerProvider';
import * as texts from '../features/dealer/textsProvider';
import * as inventory from '../features/dealer/inventoryProvider';

export type DataMode = 'live' | 'fixture';

export interface DataProvider {
  mode: DataMode;
  session: SessionApi;
  entities: EntitiesApi;
  leads: leads.Api;
  calls: calls.Api;
  jobs: jobs.Api;
  dispatch: dispatch.Api;
  techs: techs.Api;
  customers: customers.Api;
  payments: payments.Api;
  passport: passport.Api;
  parts: parts.Api;
  fleet: fleet.Api;
  today: today.Api;
  analytics: analytics.Api;
  settings: settings.Api;
  onboarding: onboarding.Api;
  /** The dealer workspace's CRM screens (features/dealer/crmProvider.ts). */
  crm: crm.Api;
  /** The dealer workspace's sales deals (features/dealer/dealsProvider.ts). */
  deals: deals.Api;
  /** The dealer workspace's scheduler screens (features/dealer/schedulerProvider.ts). */
  scheduler: scheduler.Api;
  /** The dealer workspace's Texts screens (features/dealer/textsProvider.ts). */
  texts: texts.Api;
  /** The dealer workspace's Inventory screens (features/dealer/inventoryProvider.ts). */
  inventory: inventory.Api;
  /** Fixture mode only: the in-memory tenant (its bus feeds the stream). */
  fixtureDb: FixtureDb | null;
}

export function makeLive(): DataProvider {
  return {
    mode: 'live', session: liveSession(http), entities: liveEntities(http),
    leads: leads.live(http), calls: calls.live(http), jobs: jobs.live(http), dispatch: dispatch.live(http), techs: techs.live(http), customers: customers.live(http),
    payments: payments.live(http), passport: passport.live(http), parts: parts.live(http), fleet: fleet.live(http), today: today.live(http),
    analytics: analytics.live(http), settings: settings.live(http), onboarding: onboarding.live(http), crm: crm.live(http), deals: deals.live(http), scheduler: scheduler.live(http), texts: texts.live(http), fixtureDb: null,
    inventory: inventory.live(http),
  };
}

/** Fixtures are compiled in only for dev and VITE_FIXTURES=1 builds (demo/staging); production is always live. */
export const FIXTURES_ALLOWED = import.meta.env.DEV || import.meta.env.VITE_FIXTURES === '1';
const STORE = 'rnr.dataMode';

/** `?data=live|fixture` switches (and remembers) the mode where fixtures are allowed. Default: fixture in dev. */
export function resolveMode(): DataMode {
  if (!FIXTURES_ALLOWED) return 'live';
  const q = new URLSearchParams(location.search).get('data');
  try {
    if (q === 'live' || q === 'fixture') localStorage.setItem(STORE, q);
    const s = localStorage.getItem(STORE);
    if (s === 'live' || s === 'fixture') return s;
  } catch { /* storage blocked */ }
  return q === 'live' ? 'live' : 'fixture';
}

const Ctx = createContext<{ data: DataProvider } | null>(null);

export function DataRoot({ children, fallback }: { children: ReactNode; fallback: ReactNode }) {
  const [data, setData] = useState<DataProvider | null>(null);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    let on = true;
    // `FIXTURES_ALLOWED` is a build-time constant: in a production build this branch (and the fixture chunk) is dropped.
    if (FIXTURES_ALLOWED && resolveMode() === 'fixture') {
      import('./fixtures/provider').then((m) => m.loadFixtureProvider()).then((p) => on && setData(p)).catch((e) => on && setErr(e));
    } else setData(makeLive());
    return () => { on = false; };
  }, []);
  if (err) throw err;
  if (!data) return <>{fallback}</>;
  return <Ctx.Provider value={{ data }}>{children}</Ctx.Provider>;
}

export function useData() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useData() outside <DataRoot>');
  return v;
}
